const GSM7_RUNES =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà^{}\\[~]|€";

const GSM7_SET = new Set(GSM7_RUNES);

const GSM7_EXTENDED = new Set(["^", "{", "}", "\\", "[", "~", "]", "|", "€"]);

const GSM_SINGLE = 160;
const GSM_CONCAT = 153;
const UNICODE_SINGLE = 70;
const UNICODE_CONCAT = 67;

const UNICODE_TRIGGER_HINT_MAX = 16;

export type SMSLengthInfo = {
  unicode: boolean;
  units: number;
  segmentLimit: number;
  segments: number;
};

export function needsSMSUnicode(text: string): boolean {
  for (const ch of text) {
    if (!GSM7_SET.has(ch)) return true;
  }

  return false;
}

export function listSMSUnicodeTriggers(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const ch of text) {
    if (GSM7_SET.has(ch) || seen.has(ch)) continue;
    seen.add(ch);
    out.push(ch);
  }

  return out;
}

function formatUnicodeTriggerDisplay(ch: string): string {
  if (ch === "\n") return "↵";
  if (ch === "\r") return "␍";
  if (ch === "\t") return "⇥";
  if (ch === "\u00a0") return "NBSP";
  if (ch === "\u202f") return "NNBSP";
  if (ch === "\u2019") return "’";
  if (ch === "\u2018") return "‘";
  if (ch === "\u201c") return "“";
  if (ch === "\u201d") return "”";

  return ch;
}

export function formatSMSUnicodeTriggersHint(text: string): string | null {
  const triggers = listSMSUnicodeTriggers(text);
  if (triggers.length === 0) return null;

  const shown = triggers.slice(0, UNICODE_TRIGGER_HINT_MAX).map(formatUnicodeTriggerDisplay);
  const extra =
    triggers.length > UNICODE_TRIGGER_HINT_MAX
      ? ` (+${triggers.length - UNICODE_TRIGGER_HINT_MAX})`
      : "";

  return `Hors GSM-7 : ${shown.join(" · ")}${extra}`;
}

function gsm7UnitLength(text: string): number {
  let units = 0;
  for (const ch of text) {
    units += GSM7_EXTENDED.has(ch) ? 2 : 1;
  }

  return units;
}

function segmentCount(units: number, single: number, concat: number): number {
  if (units <= 0) return 0;
  if (units <= single) return 1;

  return Math.ceil(units / concat);
}

export function getSMSLengthInfo(text: string): SMSLengthInfo {
  const unicode = needsSMSUnicode(text);
  const units = unicode ? text.length : gsm7UnitLength(text);
  const segmentLimit = unicode ? UNICODE_SINGLE : GSM_SINGLE;
  const segments = segmentCount(
    units,
    unicode ? UNICODE_SINGLE : GSM_SINGLE,
    unicode ? UNICODE_CONCAT : GSM_CONCAT,
  );

  return { unicode, units, segmentLimit, segments };
}

const SMS_PLACEHOLDER_RE = /\{\{\s*\.?[A-Za-z][A-Za-z0-9_]*\s*\}\}/;

export function smsTextHasPlaceholders(text: string): boolean {
  return SMS_PLACEHOLDER_RE.test(text);
}

export function getSMSCharsPerSegment(info: SMSLengthInfo): number {
  if (info.segments <= 1) return info.segmentLimit;

  return info.unicode ? UNICODE_CONCAT : GSM_CONCAT;
}

export function getSMSDispatchTotal(text: string, dispatchCount: number): number {
  if (dispatchCount < 1) return 0;
  const segments = getSMSLengthInfo(text).segments;
  if (segments < 1) return 0;

  return segments * dispatchCount;
}
